<template>
  <div :key="switch1">
    <loading-component :value="loading" />

    <div class="page-wrap">
      <div class="responsive-toolbar">
        <div class="toolbar-top">
          <div class="toolbar-title">Admin Transfers</div>
          <div class="balance-label">
            Balance: <span class="balance-value">{{ balance | money }}</span>
          </div>
        </div>

        <div class="toolbar-controls">
          <choose-date class="control-field" label="From" v-model="fromDate"></choose-date>
          <choose-date class="control-field" label="To" v-model="toDate"></choose-date>
          <v-btn color="primary" dark class="mb-2 cbtn control-btn" @click="getAccountingData(false)"
            :loading="loading">Get Data</v-btn>
          <v-btn color="primary" dark class="mb-2 cbtn control-btn" @click="getAccountingData(true)"
            :loading="loading">Export CSV</v-btn>
        </div>
      </div>

      <div class="table-wrap">
        <v-data-table :headers="headers" :items="accountingData" class="elevation-1" :pagination.sync="pagination"
          :rows-per-page-items="[5, 10, 15, 25, 100]" :loading="loading" :total-items="totalItems"
          :hide-headers="$vuetify.breakpoint.xsOnly">
          <template v-slot:items="props">
            <tr v-if="!$vuetify.breakpoint.xsOnly">
              <td class="text-xs-left">{{ props.item.id }}</td>
              <td class="text-xs-left">{{ props.item.amount | money }}</td>
              <td class="text-xs-left">{{ formatDate(props.item.created_at) }}</td>
            </tr>
            <tr v-else class="mobile-card-row">
              <td :colspan="headers.length" class="mobile-card-cell">
                <div class="payment-card">
                  <div class="card-row">
                    <span class="card-label">#</span>
                    <span class="card-value">{{ props.item.id }}</span>
                  </div>
                  <div class="card-row">
                    <span class="card-label">Amount</span>
                    <span class="card-value">{{ props.item.amount | money }}</span>
                  </div>
                  <div class="card-row">
                    <span class="card-label">Date</span>
                    <span class="card-value">{{ formatDate(props.item.created_at) }}</span>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </div>


  </div>
</template>

<script>
import { companyNow, companyToday, formatCompanyTime } from "@/util/companyTime";
import LoadingComponent from "@/components/LoadingComponent";
import ChooseDate from "@/components/monaco/input/ChooseDate";

export default {
  name: "AdminTransfers",
  title: "Admin Transfers",
  components: {
    LoadingComponent,
    ChooseDate
  },
  data() {
    return {
      rules: {
        length: (len) => (v) =>
          (v || "").length >= len || `Invalid character length, required ${len}`,
        required: (v) => !!v || "This field is required",
        number: (v) => /^\d+(\.\d+)?$/.test(v) || "Must be a positive number"
      },
      accountingData: [],
      loading: false,

      selectedCaptain: null,
      balance: 0,
      search: '',
      fromDate: companyNow().startOf("year").format("YYYY-MM-DD"),
      toDate: companyToday(),
      totalItems: 0,
      headers: [
        { text: '#', value: 'id' },
        { text: 'Amount', value: 'amount' },
        { text: 'Date', value: 'created_at' }
      ],
      pagination: {
        sortBy: "id",
        descending: true,
        rowsPerPage: 25,
        page: 1,
      },

      switch1: null,
      isInitialLoad: true,
    };
  },
  mounted() {
    this.fromDate=companyNow().startOf("year").format("YYYY-MM-DD")
    this.toDate= companyToday()
    this.getAccountingData();
    // Set flag to false after initial load to allow watcher to work
    this.$nextTick(() => {
      this.isInitialLoad = false;
    });
  },

  watch: {

    pagination: {
      handler() {
        // Prevent watcher from triggering on initial mount
        if (!this.isInitialLoad) {
          this.getAccountingData();
        }
      },
      deep: true,
    },
  
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      // Final-version doc 2026-10-05: Damascus time, whatever the PC zone.
      return formatCompanyTime(date, "DD-MM-YYYY HH:mm:ss", "");
    },
   
    getAccountingData(excel = false) {
      const params = new URLSearchParams();
      const sortBy = this.pagination.sortBy || "id";
      const descending = Boolean(this.pagination.descending);

      if (!this.fromDate || !this.toDate) {
        window.getApp.$emit("SHOW_TOAST", "Please select a date range", "red");
        return;
      }
      if (this.fromDate > this.toDate) {
        window.getApp.$emit("SHOW_TOAST", "From date must be before to date", "red");
        return;
      }
      this.loading = true;
      params.append("perPage", String(this.pagination.rowsPerPage));
      params.append("page", this.pagination.page);
      params.append("sortBy", sortBy);
      params.append("descending", String(descending));
      params.append("excel", excel ? 1 : 0);
      params.append("from", this.fromDate);
      params.append("to", this.toDate);
      if (this.search) {
        params.append("search", this.search);
      }


      this.axios
        .get("admin-accounting", { params: params, responseType: excel ? "blob" : "json" })
        .then(({ data }) => {
          if (!excel) {
            const responseData = data.data.payments || {};
            this.accountingData = responseData.data || [];
            this.totalItems = responseData.total || 0;
            this.balance = data.data.balance;
            if (responseData.per_page) {
              this.pagination.rowsPerPage = Number(responseData.per_page);
            }
          } else {
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Admin_transfers.csv");
            document.body.appendChild(link);
            link.click();
          }

        })
        .catch(() => {
          this.accountingData = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  
  },
};
</script>

<style scoped>
.page-wrap {
  width: 100%;
  box-sizing: border-box;
}

.responsive-toolbar {
  background: #fff;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toolbar-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
}

.toolbar-title {
  font-size: 20px;
  font-weight: 500;
  color: #333;
}

.toolbar-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
}

.control-field {
  flex: 1 1 200px;
  min-width: 160px;
  max-width: 240px;
}

.control-btn {
  flex: 0 0 auto;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.mobile-card-cell {
  padding: 12px !important;
  height: auto !important;
}

.mobile-card-row td {
  border-bottom: 8px solid #f5f5f5 !important;
}

.payment-card {
  width: 100%;
  background: #fff;
  border-radius: 6px;
  padding: 4px 8px;
}

.balance-label {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.balance-value {
  color: #27a432;
  font-weight: 700;
}

@media (max-width: 600px) {
  .responsive-toolbar {
    padding: 10px 12px;
  }
  .toolbar-title {
    font-size: 18px;
  }
  .balance-label {
    font-size: 14px;
  }
  .control-field {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
  }
  .control-btn {
    flex: 1 1 100%;
    width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
}

.filters-container {
  padding: 16px;
}

.cards-container {
  padding: 16px;
}

.card-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.card-row:last-child {
  border-bottom: none;
}

.card-label {
  font-weight: 600;
  color: #666;
  font-size: 14px;
}

.card-value {
  color: #333;
  font-size: 14px;
  text-align: right;
}

.card-value.balance {
  font-weight: 700;
  font-size: 16px;
}

.card-value.balance.positive {
  color: #4caf50;
}

.card-value.balance.negative {
  color: #f44336;
}

.card-actions {
  padding: 8px 16px;
  justify-content: flex-end;
}

.pagination-container {
  padding: 16px;
  display: flex;
  justify-content: center;
}

.date-filter {
  max-width: 170px;
  margin-right: 12px;
}
</style>
